import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import {
  activityOccursOn,
  isActivityVisibleForPet,
  parseActivityRecurrence,
  type ActivityTargetType,
  type RecurrenceRow,
} from "../activities/activitiesModel";
import { buildCheckoutDays, type CheckoutActivity, type CheckoutDay, type CheckoutObservation } from "./stayCheckoutModel";
import { fetchStayReservationMedia } from "./useStayMedia";
import type { StayMediaItem } from "./stayMediaModel";

export interface StayCheckoutSummary {
  days: CheckoutDay[];
  media: StayMediaItem[];
}

export const stayCheckoutSummaryKey = (reservationId: string | null) => ["stay-checkout-summary", reservationId];

interface RawActivity {
  id: string;
  target_type: ActivityTargetType;
  target_pet_id: string | null;
  scheduled_time: string | null;
  scheduled_date: string | null;
  description: string | null;
  stay_activity_types: { name: string } | null;
  stay_activity_recurrence: RecurrenceRow[] | null;
  stay_activity_completions: { completed_date: string }[] | null;
}

interface RawObservation {
  id: string;
  log_date: string;
  logged_at: string | null;
  description: string | null;
  stay_reservation_daily_observation_types: { name: string; is_incident: boolean } | null;
}

const EMPTY: StayCheckoutSummary = { days: [], media: [] };

/**
 * O resumo do checkout cobre a estadia inteira, não o dia aberto: é o que o
 * responsável recebe ao buscar o pet depois de cinco noites de hotel.
 *
 * Atividades não têm linha por dia — são regras com recorrência. Por isso a
 * consulta traz as ativas da clínica uma vez e projeta cada uma sobre os dias
 * da reserva com `activityOccursOn`, o mesmo cálculo da aba Atividades.
 */
export function useStayCheckoutSummary(reservationId: string | null, petId: string) {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useQuery({
    queryKey: stayCheckoutSummaryKey(reservationId),
    enabled: Boolean(reservationId && clinicId && petId),
    queryFn: async (): Promise<StayCheckoutSummary> => {
      if (!reservationId || !clinicId) return EMPTY;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: dayRows, error: daysError } = await (supabase as any)
        .from("stay_reservation_days")
        .select("stay_date")
        .eq("stay_reservation_id", reservationId)
        .neq("status", "cancelled")
        .order("stay_date");
      if (daysError) throw daysError;

      const days = ((dayRows ?? []) as Array<{ stay_date: string }>).map((row) => row.stay_date);
      if (days.length === 0) return EMPTY;

      const firstDay = days[0];
      const lastDay = days[days.length - 1];

      const [activitiesResult, observationsResult, media] = await Promise.all([
        supabase
          .from("stay_activities")
          .select(`
            id, target_type, target_pet_id, scheduled_time, scheduled_date, description,
            stay_activity_types ( name ),
            stay_activity_recurrence ( frequency, interval_days, days_of_week, days_of_month, ends_on ),
            stay_activity_completions ( completed_date )
          `)
          .eq("clinic_id", clinicId)
          .eq("is_active", true)
          .gte("stay_activity_completions.completed_date", firstDay)
          .lte("stay_activity_completions.completed_date", lastDay) as unknown as Promise<{ data: RawActivity[] | null; error: unknown }>,
        supabase
          .from("stay_reservation_daily_observations")
          .select(`
            id, log_date, logged_at, description,
            stay_reservation_daily_observation_types ( name, is_incident )
          `)
          .eq("stay_reservation_id", reservationId)
          .order("log_date") as unknown as Promise<{ data: RawObservation[] | null; error: unknown }>,
        fetchStayReservationMedia(reservationId),
      ]);
      if (activitiesResult.error) throw activitiesResult.error;
      if (observationsResult.error) throw observationsResult.error;

      const activities: CheckoutActivity[] = [];
      for (const day of days) {
        for (const row of activitiesResult.data ?? []) {
          if (!isActivityVisibleForPet(row.target_type, row.target_pet_id, petId)) continue;
          const recurrence = parseActivityRecurrence(row.stay_activity_recurrence);
          if (!activityOccursOn(row.scheduled_date, recurrence, day)) continue;

          activities.push({
            id: `${row.id}:${day}`,
            day,
            time: (row.scheduled_time ?? "").slice(0, 5),
            typeName: row.stay_activity_types?.name ?? "",
            description: row.description ?? "",
            done: (row.stay_activity_completions ?? []).some((completion) => completion.completed_date === day),
          });
        }
      }

      const observations: CheckoutObservation[] = (observationsResult.data ?? []).map((row) => ({
        id: row.id,
        day: row.log_date,
        time: (row.logged_at ?? "").slice(0, 5),
        typeName: row.stay_reservation_daily_observation_types?.name ?? "",
        description: row.description ?? "",
        isIncident: row.stay_reservation_daily_observation_types?.is_incident ?? false,
      }));

      return { days: buildCheckoutDays({ days, activities, observations }), media };
    },
  });
}
